/**
 * BarometerFilter plugin.
 *
 * Filters grant calls by labour barometer occupation names and deficit statuses.
 * Wraps the existing BarometerOccupationFilter component.
 *
 * URL params: barometerOccupation/barometerStatus (singular).
 * API/SavedView params: barometerOccupations/barometerStatuses (plural).
 *
 * Validation on fromUrlParams:
 * - Occupations filtered by regex (Latin/CE Unicode letters, digits, spaces, hyphens, dots)
 * - Statuses allowlisted against BAROMETER_STATUSES
 * - Truncation: max 3 occupations, max 4 statuses
 *
 * DD-7: Barometer is not faceted.
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4, 5)
 */

import React, { useCallback } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";
import { BAROMETER_STATUSES, DEFAULT_BAROMETER_STATUSES } from "../../../../../constants/barometer";
import BarometerOccupationFilter from "../../BarometerOccupationFilter";

/** Maximum number of occupations allowed */
const MAX_OCCUPATIONS = 3;

/** Maximum number of statuses allowed */
const MAX_STATUSES = 4;

/**
 * Regex for validating occupation names from URL params.
 * Allows word characters (including Unicode via \w), spaces, hyphens, and dots.
 * Note: \w in JS covers [a-zA-Z0-9_] but Polish diacritics pass through
 * because we also allow any non-ASCII letter via the range \u00C0-\u024F.
 */
const OCCUPATION_REGEX = /^[a-zA-Z0-9\u00C0-\u024F\u0100-\u017F _\-.]+$/;

/**
 * Value type for the barometer filter.
 */
export interface BarometerValue {
  occupations: string[];
  statuses: string[];
}

/**
 * Barometer filter component.
 * Wraps the existing BarometerOccupationFilter with plugin-compatible props.
 */
const BarometerFilterComponent = React.memo(function BarometerFilterComponent(
  props: FilterComponentProps<BarometerValue>
) {
  const { value, onChange } = props;

  const handleOccupationsChange = useCallback(
    (occupations: string[]) => {
      onChange({ ...value, occupations });
    },
    [value, onChange]
  );

  const handleStatusesChange = useCallback(
    (statuses: string[]) => {
      onChange({ ...value, statuses });
    },
    [value, onChange]
  );

  return React.createElement("div", {
    className: "border border-neutral-200 rounded-lg p-4 bg-neutral-50",
  },
    React.createElement(BarometerOccupationFilter, {
      selectedOccupations: value.occupations,
      onOccupationsChange: handleOccupationsChange,
      selectedStatuses: value.statuses.length > 0 ? value.statuses : [...DEFAULT_BAROMETER_STATUSES],
      onStatusesChange: handleStatusesChange,
    })
  );
});

export const BarometerFilterPlugin: FilterPlugin<BarometerValue> = {
  key: "barometer",
  label: "Barometr zawodów",
  placement: "advanced",
  order: 6,
  defaultValue: { occupations: [], statuses: [] },
  Component: BarometerFilterComponent,

  isActive: (value: BarometerValue): boolean => {
    return value.occupations.length > 0;
  },

  toUrlParams: (value: BarometerValue, params: URLSearchParams): void => {
    // Only serialize when occupations are present
    if (value.occupations.length === 0) return;

    value.occupations.forEach((o) =>
      params.append("barometerOccupation", o)
    );
    value.statuses.forEach((s) => params.append("barometerStatus", s));
  },

  fromUrlParams: (params: URLSearchParams): BarometerValue => {
    const rawOccupations = params.getAll("barometerOccupation");
    const rawStatuses = params.getAll("barometerStatus");

    // Validate occupations: regex filter + truncate
    const occupations = rawOccupations
      .filter((o) => OCCUPATION_REGEX.test(o))
      .slice(0, MAX_OCCUPATIONS);

    // Validate statuses: allowlist + truncate
    const validStatuses = rawStatuses.filter((s) =>
      (BAROMETER_STATUSES as readonly string[]).includes(s)
    );
    const statuses = validStatuses.slice(0, MAX_STATUSES);

    return { occupations, statuses };
  },

  toSavedView: (
    value: BarometerValue,
    filters: Partial<SavedViewFilters>
  ): void => {
    if (value.occupations.length > 0) {
      filters.barometerOccupations = value.occupations;
      filters.barometerStatuses = value.statuses;
    }
  },

  fromSavedView: (filters: SavedViewFilters): BarometerValue => {
    return {
      occupations: filters.barometerOccupations ?? [],
      statuses: filters.barometerStatuses ?? [],
    };
  },

  toApiParams: (value: BarometerValue, params: URLSearchParams): void => {
    // Only serialize when occupations are present
    if (value.occupations.length === 0) return;

    value.occupations.forEach((o) =>
      params.append("barometerOccupations", o)
    );
    value.statuses.forEach((s) =>
      params.append("barometerStatuses", s)
    );
  },
};
